import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@finora/api-client";
import { Button } from "@finora/ui";
import { api } from "../../lib/api";
import { ModuleGateNotice } from "../../components/ModuleGateNotice";

const COMING_SOON = [
  { label: "AKS kurirska služba" },
  { label: "D Express" },
];

/**
 * "Kurirske službe" - podešavanja u Company Settings (04.10.2026,
 * korisnikov zahtev - "za svaku kurirsku sluzbu stavi opciju klizac salji
 * na api ili ne"). BEX Express i Pošta Srbije su dosad JEDINI kuriri sa
 * stvarnom, potvrđenom API integracijom (BEX - WordPress plugin kod,
 * Pošta Srbije - zvanična WSP WebApi dokumentacija sa posta.rs). Ostali su
 * prikazani kao "uskoro" dok ne stigne njihova stvarna API specifikacija.
 */
export function CourierSettingsTab() {
  const gate = useQuery({
    queryKey: ["courier-addon-gate"],
    queryFn: () => api.getShipmentNotificationSettings(),
    retry: false,
  });
  if (gate.isError) {
    return (
      <div className="max-w-xl rounded-xl border border-border bg-surface p-4 shadow-subtle">
        <h2 className="mb-2 text-sm font-semibold text-slate-800">Kurirske službe</h2>
        <ModuleGateNotice error={gate.error} moduleKey="courier_shipping" />
      </div>
    );
  }
  return (
    <div className="max-w-xl space-y-10">
      <ShipmentNotificationsSection />
      <BexSection />
      <PostaSection />
      <CityExpressSection />
      <div>
        <h3 className="text-sm font-semibold text-slate-800">Ostale kurirske službe</h3>
        <p className="mt-1 text-xs text-slate-400">
          Čekamo potvrđenu API dokumentaciju od ovih kurira - dodaju se istim obrascem čim stigne.
        </p>
        <ul className="mt-3 space-y-2">
          {COMING_SOON.map((c) => (
            <li
              key={c.label}
              className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm text-slate-500"
            >
              {c.label}
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-400">Uskoro</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function BexSection() {
  const queryClient = useQueryClient();
  const { data: settings, isLoading, isError, error } = useQuery({
    queryKey: ["bex-courier-settings"],
    queryFn: () => api.getBexCourierSettings(),
    retry: false,
  });

  const [enabled, setEnabled] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [clientId, setClientId] = useState("");
  const [senderAccount, setSenderAccount] = useState("");
  const [senderPhone, setSenderPhone] = useState("");
  const [senderContactPerson, setSenderContactPerson] = useState("");

  useEffect(() => {
    if (settings) {
      setEnabled(settings.bexEnabled);
      setClientId(settings.bexClientId ?? "");
      setSenderAccount(settings.bexSenderAccount ?? "");
      setSenderPhone(settings.bexSenderPhone ?? "");
      setSenderContactPerson(settings.bexSenderContactPerson ?? "");
    }
  }, [settings]);

  const save = useMutation({
    mutationFn: () =>
      api.updateBexCourierSettings({
        bexEnabled: enabled,
        bexApiKey: apiKey || undefined,
        bexClientId: clientId || undefined,
        bexSenderAccount: senderAccount || undefined,
        bexSenderPhone: senderPhone || undefined,
        bexSenderContactPerson: senderContactPerson || undefined,
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(["bex-courier-settings"], updated);
      setApiKey("");
    },
  });

  if (isError) {
    return (
      <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
        {error instanceof Error ? error.message : "Učitavanje nije uspelo."}
      </p>
    );
  }
  if (isLoading || !settings) {
    return <p className="text-sm text-slate-500">Učitavanje...</p>;
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-sm font-semibold text-slate-800">Kurirske službe - BEX Express</h2>
        <p className="text-xs text-slate-400">
          Kad magacin izda robu po nalogu (unese serijske brojeve) i izabere BEX kao kurira,
          HAFiskal poziva BEX API, zakazuje preuzimanje paketa i vraća broj za praćenje pošiljke.
          Kupac dobija mejl ("Paket je poslat" - uredi ga u Email Template-ima) sa brojem za
          praćenje. API Key i Client ID dobijaš od BEX Express uz ugovor.
        </p>
      </div>

      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        Šalji pošiljke preko BEX API-ja (uključeno = magacin može birati BEX kurira)
      </label>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-500">API Key</label>
          <input
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            type="password"
            placeholder={settings.hasBexApiKey ? "•••••••••• (nepromenjeno)" : ""}
            className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          />
          {settings.hasBexApiKey && (
            <p className="mt-1 text-xs text-slate-400">Ključ je unet - ostavi prazno da ga zadržiš.</p>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500">Client ID</label>
          <input
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-500">Telefon pošiljaoca</label>
          <input
            value={senderPhone}
            onChange={(e) => setSenderPhone(e.target.value)}
            placeholder="npr. 0611234567"
            className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500">Kontakt osoba pošiljaoca</label>
          <input
            value={senderContactPerson}
            onChange={(e) => setSenderContactPerson(e.target.value)}
            className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-500">Broj računa za otkup (COD)</label>
        <input
          value={senderAccount}
          onChange={(e) => setSenderAccount(e.target.value)}
          placeholder="npr. 265311031000437751"
          className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
        />
        <p className="mt-1 text-xs text-slate-400">Potrebno samo za pošiljke sa otkupom (pouzećem).</p>
      </div>

      {save.isError && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {save.error instanceof ApiError ? save.error.message : "Čuvanje nije uspelo."}
        </p>
      )}
      {save.isSuccess && !save.isPending && (
        <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">Sačuvano.</p>
      )}

      <Button disabled={save.isPending} onClick={() => save.mutate()}>
        Sačuvaj
      </Button>
    </div>
  );
}

function PostaSection() {
  const queryClient = useQueryClient();
  const { data: settings, isLoading, isError, error } = useQuery({
    queryKey: ["posta-courier-settings"],
    queryFn: () => api.getPostaCourierSettings(),
    retry: false,
  });

  const [enabled, setEnabled] = useState(false);
  const [environment, setEnvironment] = useState("TEST");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [senderAccount, setSenderAccount] = useState("");
  const [senderPhone, setSenderPhone] = useState("");
  const [senderContactPerson, setSenderContactPerson] = useState("");

  useEffect(() => {
    if (settings) {
      setEnabled(settings.postaEnabled);
      setEnvironment(settings.postaEnvironment);
      setUsername(settings.postaUsername ?? "");
      setSenderAccount(settings.postaSenderAccount ?? "");
      setSenderPhone(settings.postaSenderPhone ?? "");
      setSenderContactPerson(settings.postaSenderContactPerson ?? "");
    }
  }, [settings]);

  const save = useMutation({
    mutationFn: () =>
      api.updatePostaCourierSettings({
        postaEnabled: enabled,
        postaEnvironment: environment,
        postaUsername: username || undefined,
        postaPassword: password || undefined,
        postaSenderAccount: senderAccount || undefined,
        postaSenderPhone: senderPhone || undefined,
        postaSenderContactPerson: senderContactPerson || undefined,
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(["posta-courier-settings"], updated);
      setPassword("");
    },
  });

  if (isError) {
    return (
      <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
        {error instanceof Error ? error.message : "Učitavanje nije uspelo."}
      </p>
    );
  }
  if (isLoading || !settings) {
    return <p className="text-sm text-slate-500">Učitavanje...</p>;
  }

  return (
    <div className="space-y-5 border-t border-border pt-8">
      <div>
        <h2 className="text-sm font-semibold text-slate-800">Kurirske službe - Pošta Srbije</h2>
        <p className="text-xs text-slate-400">
          Koristi zvaničan "WSP WebApi" Pošte Srbije. Adresa pošiljaoca se uzima iz podataka
          firme (ulica, broj, poštanski broj, grad - "Moj profil"). Korisničko ime i lozinku
          dobijaš mejlom od Pošte na b2b@posta.rs. Za probu možeš koristiti test okruženje -
          Pošta ima poseban demo server za to, bez stvarnog slanja pošiljaka.
        </p>
        <p className="mt-2 text-xs text-amber-600">
          Napomena: u dokumentaciji koju imamo nije pronađen poseban API za otkazivanje pošiljke
          ili preuzimanje PDF nalepnice (BEX to ima, Pošta za sada ne) - to se i dalje radi
          direktno kod Pošte.
        </p>
      </div>

      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        Šalji pošiljke preko Pošta Srbije API-ja (uključeno = magacin može birati Poštu kao kurira)
      </label>

      <div>
        <label className="block text-xs font-medium text-slate-500">Okruženje</label>
        <select
          value={environment}
          onChange={(e) => setEnvironment(e.target.value)}
          className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
        >
          <option value="TEST">Test (demo server Pošte)</option>
          <option value="PRODUCTION">Produkcija</option>
        </select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-500">Korisničko ime</label>
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="npr. TEST"
            className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500">Lozinka</label>
          <input
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            type="password"
            placeholder={settings.hasPostaPassword ? "•••••••••• (nepromenjeno)" : "npr. t3st"}
            className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          />
          {settings.hasPostaPassword && (
            <p className="mt-1 text-xs text-slate-400">Lozinka je uneta - ostavi prazno da je zadržiš.</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-500">Telefon pošiljaoca</label>
          <input
            value={senderPhone}
            onChange={(e) => setSenderPhone(e.target.value)}
            placeholder="npr. 0611234567"
            className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500">Kontakt osoba pošiljaoca</label>
          <input
            value={senderContactPerson}
            onChange={(e) => setSenderContactPerson(e.target.value)}
            className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-500">Broj računa za otkup (COD)</label>
        <input
          value={senderAccount}
          onChange={(e) => setSenderAccount(e.target.value)}
          placeholder="npr. 200-46768566-71"
          className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
        />
        <p className="mt-1 text-xs text-slate-400">Potrebno samo za pošiljke sa otkupom (pouzećem).</p>
      </div>

      {save.isError && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {save.error instanceof ApiError ? save.error.message : "Čuvanje nije uspelo."}
        </p>
      )}
      {save.isSuccess && !save.isPending && (
        <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">Sačuvano.</p>
      )}

      <Button disabled={save.isPending} onClick={() => save.mutate()}>
        Sačuvaj
      </Button>
    </div>
  );
}

function CityExpressSection() {
  const queryClient = useQueryClient();
  const { data: settings, isLoading, isError, error } = useQuery({
    queryKey: ["city-express-courier-settings"],
    queryFn: () => api.getCityExpressCourierSettings(),
    retry: false,
  });

  const [enabled, setEnabled] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [senderId, setSenderId] = useState("");

  useEffect(() => {
    if (settings) {
      setEnabled(settings.cityExpressEnabled);
      setSenderId(settings.cityExpressSenderId ?? "");
    }
  }, [settings]);

  const save = useMutation({
    mutationFn: () =>
      api.updateCityExpressCourierSettings({
        cityExpressEnabled: enabled,
        cityExpressApiKey: apiKey || undefined,
        cityExpressSenderId: senderId || undefined,
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(["city-express-courier-settings"], updated);
      queryClient.invalidateQueries({ queryKey: ["couriers"] });
      setApiKey("");
    },
  });

  if (isError) {
    return (
      <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
        {error instanceof Error ? error.message : "Učitavanje nije uspelo."}
      </p>
    );
  }
  if (isLoading || !settings) {
    return <p className="text-sm text-slate-500">Učitavanje...</p>;
  }

  return (
    <div className="space-y-5 border-t border-border pt-8">
      <div>
        <h2 className="text-sm font-semibold text-slate-800">Kurirske službe - City Express</h2>
        <p className="text-xs text-slate-400">
          API ključ dobijaš od City Expressa (isti ključ važi za pravljenje pošiljki i nalepnice).
          Adresa pošiljaoca je ona koju City Express ima za tvoj nalog. Nalepnica se štampa direktno iz HAFiskala
          (termalna ili A4).
        </p>
        <p className="mt-2 text-xs text-amber-600">
          Napomena: otkazivanje pošiljke nije dostupno preko City Express API-ja - radi se direktno kod njih.
        </p>
      </div>

      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        Šalji pošiljke preko City Express API-ja (uključeno = magacin može birati City Express kao kurira)
      </label>

      <div>
        <label className="block text-xs font-medium text-slate-500">API ključ</label>
        <input
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          type="password"
          placeholder={settings.hasCityExpressApiKey ? "•••••••••• (nepromenjeno)" : "API ključ od City Expressa"}
          className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
        />
        {settings.hasCityExpressApiKey && (
          <p className="mt-1 text-xs text-slate-400">Ključ je unet - ostavi prazno da ga zadržiš.</p>
        )}
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-500">Adresa za webhook (statusi pošiljki)</label>
        <input
          readOnly
          value={settings.cityExpressWebhookUrl}
          onFocus={(e) => e.currentTarget.select()}
          className="mt-1 h-10 w-full rounded-lg border border-border bg-slate-50 px-3 text-xs"
        />
        <p className="mt-1 text-xs text-slate-400">
          Pošalji ovu adresu City Expressu da statuse šalju HAFiskalu (bez autentifikacije - adresa je tajna, ne deli je).
          Bez webhook-a statusi se osvežavaju u 00:00 i 12:00 i na dugme "Osveži statuse".
        </p>
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-500">ID pošiljaoca (opciono)</label>
        <input
          value={senderId}
          onChange={(e) => setSenderId(e.target.value)}
          placeholder="npr. 2954"
          className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
        />
        <p className="mt-1 text-xs text-slate-400">
          Samo ako imate više pošiljalaca (adresa preuzimanja) kod City Expressa; inače ostavite prazno.
        </p>
      </div>

      {save.isError && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {save.error instanceof ApiError ? save.error.message : "Čuvanje nije uspelo."}
        </p>
      )}
      {save.isSuccess && !save.isPending && (
        <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">Sačuvano.</p>
      )}

      <Button disabled={save.isPending} onClick={() => save.mutate()}>
        Sačuvaj
      </Button>
    </div>
  );
}

function ShipmentNotificationsSection() {
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ["shipment-notification-settings"],
    queryFn: () => api.getShipmentNotificationSettings(),
    retry: false,
  });
  const save = useMutation({
    mutationFn: (payload: { notifyOnDispatch?: boolean; notifyOnStatus?: boolean }) =>
      api.updateShipmentNotificationSettings(payload),
    onSuccess: (updated) => queryClient.setQueryData(["shipment-notification-settings"], updated),
  });
  if (!data) return null;
  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-sm font-semibold text-slate-800">Mejlovi kupcu o pošiljci</h2>
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={data.notifyOnDispatch}
          disabled={save.isPending}
          onChange={(e) => save.mutate({ notifyOnDispatch: e.target.checked })}
        />
        Pošalji kupcu mejl čim se paket pošalje kuriru
      </label>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={data.notifyOnStatus}
          disabled={save.isPending}
          onChange={(e) => save.mutate({ notifyOnStatus: e.target.checked })}
        />
        Pošalji kupcu mejl kad se status isporuke promeni (preuzeto, na dostavi, isporučeno, neuspela isporuka)
      </label>
      {save.isError && <p className="text-xs text-red-700">Čuvanje nije uspelo.</p>}
    </div>
  );
}
